/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { dropTask } from 'ember-concurrency';

import TransactionalMessageModel from 'cio/models/transactional-message';
import AlertsService from 'cio/services/alerts';
import ApiService from 'cio/services/api';
import EnvironmentService from 'cio/services/environment';
import ApiErrorsService from 'cio/services/api-errors';
import AnalyticsService from 'cio/services/analytics';

interface TransactionalMessageActionDropdownArgs {
  message: TransactionalMessageModel;
  onDeleteMessage: () => unknown;
  reloadMessageModel: () => unknown;
  onStopMessage?: () => Promise<unknown>;
}

enum ModalStates {
  Closed = 'closed',
  Stop = 'stop',
  CopyToEnv = 'copyToEnv',
  Delete = 'delete',
}

type EnvData = {
  id?: string;
  name?: string;
};

export default class TransactionalMessageActionDropdown extends Component<TransactionalMessageActionDropdownArgs> {
  @service api!: ApiService;
  @service analytics!: AnalyticsService;
  @service environment!: EnvironmentService;
  @service apiErrors!: ApiErrorsService;
  @service alerts!: AlertsService;

  ModalStates = ModalStates;

  @tracked modalState: ModalStates = ModalStates.Closed;

  get actions() {
    return [
      { label: 'Duplicate', icon: 'duplicate', action: this.startDuplicate },
      { label: 'Copy to', icon: 'copy-to', action: this.startCopyToEnv },
      this.args?.message?.state === 'active'
        ? {
            label: 'Stop scheduled messages',
            icon: 'stop',
            classNames: '',
            action: this.startStop,
          }
        : null,
      { label: 'Delete', icon: 'delete', classNames: 'text-error', action: this.startDelete },
    ].filter((a) => a != null);
  }

  deleteTask = dropTask(async () => {
    try {
      await this.args.message.destroyRecord().then(() => {
        this.modalState = ModalStates.Closed;
        this.args.onDeleteMessage();
      });
    } catch (err) {
      this.apiErrors.report(err);
    }
  });

  stopTask = dropTask(async () => {
    try {
      await this.api.post(`transactional_messages/${this.args.message.id}/cancel`);

      if (this.args.onStopMessage != null) {
        await this.args.onStopMessage();
      }

      this.modalState = ModalStates.Closed;
    } catch (err) {
      this.apiErrors.report(err);
    }
  });

  copyTask = dropTask(async (env: EnvData) => {
    try {
      await this.onMessageCopy(this.args.message, env);
    } catch (e) {
      // Ignore promise failure
    } finally {
      this.modalState = ModalStates.Closed;
    }
  });

  @action
  startDelete() {
    this.modalState = ModalStates.Delete;

    this.analytics.track('transactional', 'delete_started', {
      model: this.args.message,
    });
  }

  @action
  startStop() {
    this.modalState = ModalStates.Stop;
  }

  @action
  async startDuplicate() {
    try {
      await this.onMessageCopy(this.args.message);
      this.args.reloadMessageModel();
    } catch (e) {
      // Ignore promise failure
    } finally {
      this.analytics.track('transactional', 'copy_started', { model: this.args.message });
    }
  }

  @action
  startCopyToEnv() {
    this.modalState = ModalStates.CopyToEnv;

    this.analytics.track('transactional', 'copy_to_workspace_started', { model: this.args.message });
  }

  private async onMessageCopy(message: TransactionalMessageModel, env?: EnvData) {
    try {
      await message.copy({ envID: env?.id });

      let successMessage = isEmpty(env) ? 'Copy created' : `Copy created in ${env?.name}`;
      if (isEmpty(env)) {
        this.args.reloadMessageModel();
      }
      this.alerts.success('transactional.message.copy.success', successMessage);
    } catch (e) {
      // Ignore promise failure
    }
  }
}
